import { Component, Inject, ViewChild, HostListener, OnDestroy, computed, Signal, WritableSignal, signal } from '@angular/core';
import { SeSuggestionSelectorOverlayRef } from './overlay/suggestion-selector-overlay-ref';
import {
    SUGGESTION_STREAM_TOKEN,
    FILTER_STREAM_TOKEN,
    LIGHT_LAYOUT_TOKEN,
    DEFAULT_TEXT_NAME_TOKEN,
    TEXTS_GROUP_TOKEN,
} from './overlay/suggestion-selector-overlay.token';
import {
    CpControlSuggestions,
    ExtendedSuggestion,
    SaveExtendedSuggestionEvent,
    Suggestion,
} from '../se-smart-text-editor/suggestion-datasource/suggestion';
import { SeSuggestionSelectorComponent } from '../se-suggestion-selector/se-suggestion-selector.component';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SuggestionFilter } from '../se-smart-text-editor/suggestion-datasource/suggestion-filter';
import { SeUiDefaultTextComponent } from '../se-ui-default-text/se-ui-default-text.component';

@Component({
    selector: 'se-suggestion-selector-overlay',
    templateUrl: './se-suggestion-selector-overlay.component.html',
    styleUrls: ['./se-suggestion-selector-overlay.component.scss'],
    standalone: false,
    
})
export class SeSuggestionSelectorOverlayComponent implements OnDestroy {
    @ViewChild(SeSuggestionSelectorComponent) private suggestionSelectorComponent: SeSuggestionSelectorComponent;
    @ViewChild(SeUiDefaultTextComponent) private seUiDeafaultTextComponent: SeUiDefaultTextComponent;

    filteredSuggestions= signal<Suggestion[]>([]) ;
    currentFilter: SuggestionFilter;

    key: WritableSignal<string> = signal('');
    private unsubscribe$ = new Subject<void>();
    _isLightViewConfiguration = false;
    _addNewSuggestion = false;
    _editingView = false;
    _newTextsGroup: Map<string, CpControlSuggestions>;

    filteredItems = computed(() => {
        const filteredSuggestions = this.filteredSuggestions();
        const key = this.key();

        if (!!!key) {
           return filteredSuggestions;
        }

        return filteredSuggestions
            ?.filter((p) => {
                return p.Description.toLowerCase().includes(key) || p.code?.toLowerCase().includes(key);
            })
            .sort((a, b) => (a.Description.toLowerCase() > b.Description.toLowerCase() ? 1 : -1));
    });

    constructor(
        public suggestionSelectorOverlayRef: SeSuggestionSelectorOverlayRef,
        @Inject(SUGGESTION_STREAM_TOKEN) public filteredSuggestionStream: Observable<Suggestion[]>,
        @Inject(FILTER_STREAM_TOKEN) public filterStream: Observable<SuggestionFilter>,
        @Inject(LIGHT_LAYOUT_TOKEN) public lightLayout: boolean,
        @Inject(DEFAULT_TEXT_NAME_TOKEN) public defaultTextName: string,
        @Inject(TEXTS_GROUP_TOKEN) public cpTextsStream: Observable<Map<string, CpControlSuggestions>>,
    ) {
        filteredSuggestionStream.pipe(takeUntil(this.unsubscribe$)).subscribe((newFilteredSugegstions) => {
            this.filteredSuggestions.set(newFilteredSugegstions);
        });
        filterStream.pipe(takeUntil(this.unsubscribe$)).subscribe((newFilter) => {
            this.currentFilter = newFilter;
        });
        cpTextsStream.pipe(takeUntil(this.unsubscribe$)).subscribe((newTextsGroup) => {
            this._newTextsGroup = newTextsGroup;
        });

        this.suggestionSelectorOverlayRef.preSelectNextEventEmitter
            .pipe(takeUntil(this.unsubscribe$)) // unsubscribe the original observable until unsubscribe$ emits a value
            .subscribe(() => {
                !!this.lightLayout
                    ? this.suggestionSelectorComponent.preSelectNext()
                    : this.seUiDeafaultTextComponent.preSelectNext();
            });
        this.suggestionSelectorOverlayRef.preSelectPreviousEventEmitter
            .pipe(takeUntil(this.unsubscribe$)) // unsubscribe the original observable until unsubscribe$ emits a value
            .subscribe(() => {
                !!this.lightLayout
                    ? this.suggestionSelectorComponent.preSelectPrevious()
                    : this.seUiDeafaultTextComponent.preSelectPrevious();
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getKeyCode(event: KeyboardEvent) {
        let code = null;
        if (event.code !== undefined) {
            code = event.code;
        } else if (event.key !== undefined) {
            code = event.key;
        }
        return code;
    }

    @HostListener('document:keyup', ['$event']) handleKeydown(event: KeyboardEvent) {
        const key = this.getKeyCode(event);
        if (['Escape', 'Esc'].includes(key)) {
            this.suggestionSelectorOverlayRef.close();
            event.preventDefault();
        } else if (['ArrowUp', 'Up'].includes(key)) {
            this.suggestionSelectorOverlayRef.preSelectPrevious();
            event.preventDefault();
        } else if (['ArrowDown', 'Down'].includes(key)) {
            this.suggestionSelectorOverlayRef.preSelectNext();
            event.preventDefault();
        }
    }

    public onPreselectedSuggestion(preSelectedSuggestion: Suggestion) {
        this.suggestionSelectorOverlayRef.preSelectedSuggestionEmitter.emit(preSelectedSuggestion);
    }

    public onSelectorSuggestionSelected(extendedSuggestion: ExtendedSuggestion) {
        this.suggestionSelectorOverlayRef.selectedSuggestionEventEmitter.emit(extendedSuggestion);
        this.suggestionSelectorOverlayRef.close();
    }

    onSelectorCloseCommand() {
        this.suggestionSelectorOverlayRef.close();
    }

    onSelectorUndoCommand() {
        this._addNewSuggestion = false;
    }

    onEditing($event: boolean) {
        this._editingView = $event;
        this._addNewSuggestion = false;
    }

    public _onClickAddNew() {
        this._addNewSuggestion = true;
        this._editingView = true;
    }

    updateSuggestions(event: SaveExtendedSuggestionEvent) {
        this.suggestionSelectorOverlayRef.updatedSuggestionsEventEmitter.emit(event);
    }

    setFilter(event: any) {
        const key : string = event.target.value.toLowerCase();
        this.key.set(key);
    }
}
